import type { AiCompressedRetrievalSlice, AiRankedRetrievalRecord, AiRetrievalLayer, AiRetrievalScope } from './retrievalTypes'

const LAYER_ORDER: AiRetrievalLayer[] = [
  'immediate_request',
  'active_operational',
  'dependency',
  'execution_risk',
  'governance',
  'historical',
  'strategic_memory',
]

const recordLine = (record: AiRankedRetrievalRecord) => {
  const details = [
    record.type,
    record.status ? `status=${record.status}` : '',
    record.workflowStatus ? `workflow=${record.workflowStatus}` : '',
    typeof record.progress === 'number' ? `progress=${record.progress}` : '',
    record.dueDateIso ? `due=${record.dueDateIso}` : '',
    record.isBlocked ? 'blocked=true' : '',
    record.riskSignals?.length ? `risk=${record.riskSignals.slice(0, 2).join('|')}` : '',
  ].filter(Boolean).join(' ')
  return `- ${record.title}: ${record.summary}${details ? ` (${details})` : ''}`
}

const trimToBudget = (text: string, maxCharacters: number) => {
  if (text.length <= maxCharacters) return text
  const trimmed = text.slice(0, Math.max(0, maxCharacters - 24)).trimEnd()
  return `${trimmed}\n[compressed]`
}

export const compressRetrievalRecords = (
  records: AiRankedRetrievalRecord[],
  options: { maxSlices: number; maxCharacters: number },
): AiCompressedRetrievalSlice[] => {
  const grouped = new Map<string, AiRankedRetrievalRecord[]>()
  records.forEach((record) => {
    const key = `${record.layer}:${record.scope}`
    grouped.set(key, [...(grouped.get(key) ?? []), record])
  })

  const slices: AiCompressedRetrievalSlice[] = []
  LAYER_ORDER.forEach((layer) => {
    const layerEntries = [...grouped.entries()]
      .filter(([key]) => key.startsWith(`${layer}:`))
      .sort((left, right) => {
        const leftScore = left[1][0]?.ranking.score ?? 0
        const rightScore = right[1][0]?.ranking.score ?? 0
        return rightScore - leftScore
      })
    layerEntries.forEach(([key, layerRecords]) => {
      const scope = key.split(':')[1] as AiRetrievalScope
      const included = layerRecords.slice(0, 5)
      const rawText = included.map(recordLine).join('\n')
      const characterBudget = Math.max(320, Math.floor(options.maxCharacters / Math.max(1, options.maxSlices)))
      slices.push({
        id: `slice_${layer}_${scope}`,
        scope,
        layer,
        title: `${layer.replace(/_/g, ' ')} / ${scope.replace(/_/g, ' ')}`,
        text: trimToBudget(rawText, characterBudget),
        recordIds: included.map((record) => record.id),
        score: included.reduce((sum, record) => sum + record.ranking.score, 0),
        sourceCount: layerRecords.length,
        omittedCount: Math.max(0, layerRecords.length - included.length),
      })
    })
  })

  return slices
    .sort((left, right) => right.score - left.score || left.title.localeCompare(right.title))
    .slice(0, options.maxSlices)
}

/**
 * Cite-tagged compression: same structure as compressRetrievalRecords but
 * each bullet is prefixed with a stable `[R-N]` handle mapped to the
 * underlying record id. Returns both the slice set and the handle→recordId
 * dictionary the orchestrator uses to resolve citations after model return.
 */
export const compressWithCiteTags = (
  records: AiRankedRetrievalRecord[],
  options: { maxSlices: number; maxCharacters: number },
): { slices: AiCompressedRetrievalSlice[]; handleToRecordId: Record<string, string> } => {
  const handleToRecordId: Record<string, string> = {}
  const handleFor = (recordId: string) => {
    const existing = Object.entries(handleToRecordId).find(([, id]) => id === recordId)?.[0]
    if (existing) return existing
    const handle = `R-${Object.keys(handleToRecordId).length + 1}`
    handleToRecordId[handle] = recordId
    return handle
  }

  const grouped = new Map<string, AiRankedRetrievalRecord[]>()
  records.forEach((record) => {
    const key = `${record.layer}:${record.scope}`
    grouped.set(key, [...(grouped.get(key) ?? []), record])
  })

  const slices: AiCompressedRetrievalSlice[] = []
  LAYER_ORDER.forEach((layer) => {
    const layerEntries = [...grouped.entries()]
      .filter(([key]) => key.startsWith(`${layer}:`))
      .sort((left, right) => {
        const leftScore = left[1][0]?.ranking.score ?? 0
        const rightScore = right[1][0]?.ranking.score ?? 0
        return rightScore - leftScore
      })
    layerEntries.forEach(([key, layerRecords]) => {
      const scope = key.split(':')[1] as AiRetrievalScope
      const included = layerRecords.slice(0, 5)
      const rawText = included.map((record) => {
        const details = [
          record.type,
          record.status ? `status=${record.status}` : '',
          record.workflowStatus ? `workflow=${record.workflowStatus}` : '',
          typeof record.progress === 'number' ? `progress=${record.progress}` : '',
          record.dueDateIso ? `due=${record.dueDateIso}` : '',
        ].filter(Boolean).join(' ')
        return `- [${handleFor(record.id)}] ${record.title}: ${record.summary}${details ? ` (${details})` : ''}`
      }).join('\n')
      const characterBudget = Math.max(320, Math.floor(options.maxCharacters / Math.max(1, options.maxSlices)))
      slices.push({
        id: `slice_${layer}_${scope}`,
        scope,
        layer,
        title: `${layer.replace(/_/g, ' ')} / ${scope.replace(/_/g, ' ')}`,
        text: trimToBudget(rawText, characterBudget),
        recordIds: included.map((record) => record.id),
        score: included.reduce((sum, record) => sum + record.ranking.score, 0),
        sourceCount: layerRecords.length,
        omittedCount: Math.max(0, layerRecords.length - included.length),
      })
    })
  })

  const orderedSlices = slices
    .sort((left, right) => right.score - left.score || left.title.localeCompare(right.title))
    .slice(0, options.maxSlices)

  return { slices: orderedSlices, handleToRecordId }
}

export const summarizeCompressedSlices = (slices: AiCompressedRetrievalSlice[]) => {
  if (!slices.length) return 'No relevant operational retrieval slices were assembled.'
  return slices.map((slice) => [
    `[${slice.title}]`,
    slice.text,
    slice.omittedCount ? `Omitted lower-priority records in this slice: ${slice.omittedCount}.` : '',
  ].filter(Boolean).join('\n')).join('\n\n')
}
